/**
 * Polyline - Multi-segment line through multiple points
 *
 * Rhino-style: Polylines can reference Point objects via pointIds.
 * When the referenced points move, the polyline updates automatically.
 */

import { GeometryType } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { CurveBase } from './CurveBase';
import { Line } from './Line';

export class Polyline extends CurveBase {
  /**
   * Create a polyline
   * @param {Array<Vector|{x,y,z}>} points - Array of points (or empty if using pointIds)
   * @param {boolean} closed - Whether polyline is closed
   * @param {Object} attributes - Including optional pointIds array
   */
  constructor(points = [], closed = false, attributes = {}) {
    super(GeometryType.POLYLINE, 'pl', attributes);

    // Extract pointIds from attributes
    const pointIds = attributes.pointIds || null;
    this._pointIds = pointIds;

    if (points.length > 0) {
      this._points = points.map(p => p instanceof Vector ? p : Vector.fromObject(p));
    } else if (pointIds && pointIds.length > 0) {
      // Create placeholder vectors - will be filled from external state
      this._points = pointIds.map(() => new Vector(0, 0, 0));
    } else {
      this._points = [];
    }
    this.closed = closed;
  }

  /**
   * Get points array (for compatibility)
   */
  get points() {
    return this._points;
  }

  /**
   * Set points array (for compatibility)
   */
  set points(pts) {
    this._points = pts.map(p => p instanceof Vector ? p : Vector.fromObject(p));
  }

  /**
   * Get point IDs array
   */
  get pointIds() {
    return this._pointIds;
  }

  /**
   * Set point IDs
   */
  set pointIds(ids) {
    this._pointIds = ids;
    this.touch();
  }

  /**
   * Check if this polyline uses point references
   */
  get usesPointReferences() {
    return this._pointIds && this._pointIds.length > 0;
  }

  /**
   * Update internal vectors from external point state
   * @param {Function} getPoint - Function to get point by ID (returns {x, y, z})
   */
  updateFromPoints(getPoint) {
    if (!this.usesPointReferences) return;

    this._points = this._pointIds.map(id => {
      const pt = getPoint(id);
      return pt ? new Vector(pt.x, pt.y, pt.z || 0) : new Vector(0, 0, 0);
    });
  }

  /**
   * Get center point of polyline (average of all points)
   */
  get centerPoint() {
    if (this._points.length === 0) return Vector.zero();
    const sum = this._points.reduce((acc, p) => acc.add(p), Vector.zero());
    return sum.multiply(1 / this._points.length);
  }

  get isClosed() {
    return this.closed;
  }

  get segmentCount() {
    if (this.points.length < 2) return 0;
    return this.closed ? this.points.length : this.points.length - 1;
  }

  get length() {
    let total = 0;
    for (let i = 0; i < this.segmentCount; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % this.points.length];
      total += p1.distanceTo(p2);
    }
    return total;
  }

  /**
   * Get segment lengths
   * @returns {number[]}
   */
  get segmentLengths() {
    const lengths = [];
    for (let i = 0; i < this.segmentCount; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % this.points.length];
      lengths.push(p1.distanceTo(p2));
    }
    return lengths;
  }

  /**
   * Get cumulative lengths for parameterization
   * @returns {number[]}
   */
  get cumulativeLengths() {
    const lengths = [0];
    let total = 0;
    for (const len of this.segmentLengths) {
      total += len;
      lengths.push(total);
    }
    return lengths;
  }

  pointAt(t) {
    if (this.points.length === 0) return Vector.zero();
    if (this.points.length === 1) return this.points[0];

    t = Math.max(0, Math.min(1, t));
    const totalLength = this.length;
    const targetLength = t * totalLength;
    const cumLengths = this.cumulativeLengths;

    // Find which segment contains this parameter
    for (let i = 0; i < this.segmentCount; i++) {
      if (targetLength <= cumLengths[i + 1]) {
        const segStart = cumLengths[i];
        const segLength = cumLengths[i + 1] - segStart;
        const localT = segLength > 0 ? (targetLength - segStart) / segLength : 0;
        const p1 = this.points[i];
        const p2 = this.points[(i + 1) % this.points.length];
        return p1.lerp(p2, localT);
      }
    }

    return this.closed ? this.points[0] : this.points[this.points.length - 1];
  }

  tangentAt(t) {
    if (this.points.length < 2) return Vector.unitX();

    t = Math.max(0, Math.min(1, t));
    const totalLength = this.length;
    const targetLength = t * totalLength;
    const cumLengths = this.cumulativeLengths;

    for (let i = 0; i < this.segmentCount; i++) {
      if (targetLength <= cumLengths[i + 1]) {
        const p1 = this.points[i];
        const p2 = this.points[(i + 1) % this.points.length];
        return p2.subtract(p1).normalize();
      }
    }

    const last = this.segmentCount - 1;
    const p1 = this.points[last];
    const p2 = this.points[(last + 1) % this.points.length];
    return p2.subtract(p1).normalize();
  }

  get startPoint() {
    return this.points[0] || Vector.zero();
  }

  get endPoint() {
    if (this.closed) return this.points[0] || Vector.zero();
    return this.points[this.points.length - 1] || Vector.zero();
  }

  /**
   * Get segment as Line
   * @param {number} index - Segment index
   * @returns {Line}
   */
  getSegment(index) {
    const p1 = this.points[index];
    const p2 = this.points[(index + 1) % this.points.length];
    return new Line(p1, p2);
  }

  /**
   * Add point to end
   * @param {Vector|{x,y,z}} point
   */
  addPoint(point) {
    this.points.push(point instanceof Vector ? point : Vector.fromObject(point));
    this.touch();
  }

  /**
   * Insert point at index
   * @param {number} index
   * @param {Vector|{x,y,z}} point
   */
  insertPoint(index, point) {
    const p = point instanceof Vector ? point : Vector.fromObject(point);
    this.points.splice(index, 0, p);
    this.touch();
  }

  /**
   * Remove point at index
   * @param {number} index
   */
  removePoint(index) {
    this.points.splice(index, 1);
    this.touch();
  }

  /**
   * Move point at index
   * @param {number} index
   * @param {Vector|{x,y,z}} newPosition
   */
  movePoint(index, newPosition) {
    this.points[index] = newPosition instanceof Vector ? newPosition : Vector.fromObject(newPosition);
    this.touch();
  }

  /**
   * Close or open the polyline
   * @param {boolean} closed
   */
  setClosed(closed) {
    this.closed = closed;
    this.touch();
  }

  /**
   * Offset polyline
   * @param {number} distance - Offset distance (positive = left)
   * @returns {Polyline}
   */
  offset(distance) {
    if (this.points.length < 2) return this.clone();

    const offsetPoints = [];
    const n = this.points.length;

    for (let i = 0; i < n; i++) {
      const prevIdx = (i - 1 + n) % n;
      const nextIdx = (i + 1) % n;

      // Handle open polyline endpoints
      if (!this.closed && i === 0) {
        const dir = this.points[1].subtract(this.points[0]).normalize();
        const perp = new Vector(-dir.y, dir.x, 0).multiply(distance);
        offsetPoints.push(this.points[0].add(perp));
        continue;
      }
      if (!this.closed && i === n - 1) {
        const dir = this.points[n - 1].subtract(this.points[n - 2]).normalize();
        const perp = new Vector(-dir.y, dir.x, 0).multiply(distance);
        offsetPoints.push(this.points[n - 1].add(perp));
        continue;
      }

      // Get directions
      const dirPrev = this.points[i].subtract(this.points[prevIdx]).normalize();
      const dirNext = this.points[nextIdx].subtract(this.points[i]).normalize();

      // Perpendiculars
      const perpPrev = new Vector(-dirPrev.y, dirPrev.x, 0);
      const perpNext = new Vector(-dirNext.y, dirNext.x, 0);

      // Bisector
      const bisector = perpPrev.add(perpNext).normalize();
      const dot = perpPrev.dot(bisector);
      const offsetDist = dot !== 0 ? distance / dot : distance;

      offsetPoints.push(this.points[i].add(bisector.multiply(offsetDist)));
    }

    return new Polyline(offsetPoints, this.closed, { ...this.attributes });
  }

  /**
   * Reverse point order
   * @returns {Polyline}
   */
  reverse() {
    return new Polyline([...this.points].reverse(), this.closed, { ...this.attributes });
  }

  /**
   * Get area (only for closed polylines)
   * @returns {number} Area in mm² (positive = CCW, negative = CW)
   */
  get area() {
    if (!this.closed || this.points.length < 3) return 0;

    let area = 0;
    const n = this.points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += this.points[i].x * this.points[j].y;
      area -= this.points[j].x * this.points[i].y;
    }
    return area / 2;
  }

  /**
   * Check if polyline is clockwise
   * @returns {boolean}
   */
  get isClockwise() {
    return this.area < 0;
  }

  clone() {
    const pl = new Polyline(
      this._points.map(p => p.clone()),
      this.closed,
      { ...this.attributes }
    );
    pl._pointIds = this._pointIds ? [...this._pointIds] : null;
    return pl;
  }

  transform({ translate, rotate, scale, mirror }) {
    const newPoints = this.points.map(p => {
      let pt = p.clone();
      if (scale) pt = pt.multiply(typeof scale === 'number' ? scale : 1);
      if (rotate) pt = pt.rotate2D(rotate);
      if (translate) pt = pt.add(Vector.fromObject(translate));
      if (mirror) pt = pt.mirror(Vector.fromObject(mirror));
      return pt;
    });
    return new Polyline(newPoints, this.closed, { ...this.attributes });
  }

  getBoundingBox() {
    if (this.points.length === 0) {
      return { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } };
    }

    const xs = this.points.map(p => p.x);
    const ys = this.points.map(p => p.y);
    const zs = this.points.map(p => p.z);

    return {
      min: { x: Math.min(...xs), y: Math.min(...ys), z: Math.min(...zs) },
      max: { x: Math.max(...xs), y: Math.max(...ys), z: Math.max(...zs) },
    };
  }

  getDisplayPoints() {
    if (this.closed && this.points.length > 0) {
      return [...this.points, this.points[0]];
    }
    return [...this.points];
  }

  toJSON() {
    return {
      ...super.toJSON(),
      points: this._points.map(p => p.toObject()),
      closed: this.closed,
      pointIds: this._pointIds || null,
    };
  }

  static fromJSON(json) {
    const pl = new Polyline(
      json.points.map(p => Vector.fromObject(p)),
      json.closed,
      { ...json.attributes, pointIds: json.pointIds || null }
    );
    pl.id = json.id;
    pl.createdAt = json.createdAt;
    pl.modifiedAt = json.modifiedAt;
    return pl;
  }
}

export default Polyline;
